/**
 * Integration Configuration System
 * 
 * This file defines the complete configuration for each integration type,
 * including required fields, field labels, validation rules, and help text.
 * 
 * Purpose: Simplify the AppSecrets UI by showing only relevant fields for each integration.
 */

/**
 * Field type definitions used across integrations
 */
export const FieldTypes = {
    TEXT: 'text',
    PASSWORD: 'password',
    TEXTAREA: 'textarea',
    NUMBER: 'number',
    CHECKBOX: 'checkbox',
    SELECT: 'select',
    URL: 'url'
};

/**
 * Integration categories for grouping
 */
export const IntegrationCategories = {
    IT_SYSTEMS: 'IT Systems',
    REMOTE_SUPPORT: 'Remote Support',
    OPERATIONS: 'Operations',
    EXTERNAL_APIS: 'External APIs',
    DATABASES: 'Databases',
    AZURE_CLOUD: 'Azure & Cloud',
    EMAIL_MESSAGING: 'Email & Messaging',
    COLLABORATION: 'Collaboration & Messaging',
    CONFIGURATION: 'Configuration'
};

/**
 * Complete integration configurations
 * Each integration defines its own set of fields with specific labels and validation
 */
export const IntegrationConfigs = {
    // ===== IT SYSTEMS =====

    proxmox: {
        name: 'Proxmox Virtualization',
        category: IntegrationCategories.IT_SYSTEMS,
        secretType: 'SimpleCredential',
        description: 'Manage virtual machines and containers with Proxmox VE',
        fields: [
            {
                key: 'username',
                label: 'Token ID',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'user@realm!tokenid',
                help: 'API token identifier in the format: user@realm!tokenid (e.g., root@pam!monitoring)'
            },
            {
                key: 'password',
                label: 'Token Secret',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'The secret value for the API token'
            },
            {
                key: 'baseUrl',
                label: 'Proxmox API URL',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'https://proxmox.example.com:8006/api2/json',
                help: 'Proxmox API endpoint URL'
            },
            {
                key: 'allowInsecure',
                label: 'Allow Insecure Connections',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable this if using self-signed SSL certificates'
            }
        ]
    },

    MsPurview: {
        name: 'Microsoft Purview (via Azure AD)',
        category: IntegrationCategories.AZURE_CLOUD,
        secretType: 'AzureAd',
        description: 'Data governance and compliance management - uses same Azure AD app registration as Defender, SharePoint, Exchange, and Intune',
        fields: [
            {
                key: 'azureTenantId',
                label: 'Azure AD Tenant ID',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
                help: 'Azure AD tenant GUID (found in Azure Portal)'
            },
            {
                key: 'azureClientId',
                label: 'Application (Client) ID',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
                help: 'Azure AD application/client ID for service principal'
            },
            {
                key: 'azureClientSecret',
                label: 'Client Secret',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'Azure AD application client secret value'
            },
            {
                key: 'baseUrl',
                label: 'Graph API URL',
                type: FieldTypes.URL,
                required: false,
                placeholder: 'https://graph.microsoft.com',
                help: 'Microsoft Graph API base URL (leave default unless using sovereign cloud)'
            }
        ]
    },

    // ===== OPERATIONS =====
    rubrik: {
        name: 'Rubrik Backup',
        category: IntegrationCategories.OPERATIONS,
        secretType: 'OAuth',
        description: 'Enterprise data protection and backup management',
        fields: [
            {
                key: 'baseUrl',
                label: 'Base URL',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'https://capitallandmanagement.my.rubrik.com',
                help: 'Rubrik cluster or Polaris base URL'
            },
            {
                key: 'oAuthClientId',
                label: 'Client ID',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'client|7e708f86-a7be-3df1-b051-be38e5f16201',
                help: 'OAuth 2.0 Client ID from Rubrik (format: client|guid)'
            },
            {
                key: 'oAuthClientSecret',
                label: 'Client Secret',
                type: FieldTypes.PASSWORD,
                required: true,
                placeholder: 'dBq2E0LU3gYoapdwnlXmjeSSHgVfEelL...',
                help: 'OAuth 2.0 Client Secret from Rubrik'
            },
            {
                key: 'secondaryUrl',
                label: 'Access Token URI',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'https://capitallandmanagement.my.rubrik.com/api/client_token',
                help: 'OAuth token endpoint URL (typically base URL + /api/client_token)'
            },
            {
                key: 'allowInsecure',
                label: 'Allow Insecure Connections',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable this if using self-signed or expired SSL certificates'
            }
        ]
    },

    porta: {
        name: 'PortaOne VoIP Billing',
        category: IntegrationCategories.OPERATIONS,
        secretType: 'SimpleCredential',
        description: 'VoIP billing and call detail record management',
        fields: [
            {
                key: 'username',
                label: 'PortaOne Username',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'admin',
                help: 'PortaOne account username'
            },
            {
                key: 'password',
                label: 'PortaOne Password',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'PortaOne account password'
            },
            {
                key: 'apiKey',
                label: 'API Token (Optional)',
                type: FieldTypes.PASSWORD,
                required: false,
                help: 'Optional API token for enhanced security'
            },
            {
                key: 'baseUrl',
                label: 'SOAP API URL',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'https://porta.example.com:8444/soap/',
                help: 'PortaOne SOAP API endpoint'
            },
            {
                key: 'customerId',
                label: 'Customer ID',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: '1878',
                help: 'Your PortaOne customer identifier'
            },
            {
                key: 'maxItems',
                label: 'Max CDR Rows',
                type: FieldTypes.NUMBER,
                required: false,
                placeholder: '100000',
                help: 'Maximum call detail records per request'
            },
            {
                key: 'timeoutSeconds',
                label: 'Timeout (seconds)',
                type: FieldTypes.NUMBER,
                required: false,
                placeholder: '300',
                help: 'API request timeout in seconds'
            },
            {
                key: 'allowInsecure',
                label: 'Allow Insecure Connections',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable this if using self-signed or expired SSL certificates'
            }
        ]
    },

    rms: {
        name: 'RMS Meters',
        category: IntegrationCategories.OPERATIONS,
        secretType: 'SimpleCredential',
        description: 'Energy meter data collection and monitoring',
        fields: [
            {
                key: 'username',
                label: 'RMS Username',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'your.email@company.com',
                help: 'RMS account username or email'
            },
            {
                key: 'password',
                label: 'RMS Password',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'RMS account password'
            },
            {
                key: 'baseUrl',
                label: 'RMS API URL',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'https://meters.rmsconnect.net/api',
                help: 'RMS API endpoint'
            },
            {
                key: 'allowInsecure',
                label: 'Allow Insecure Connections',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable this if using self-signed or expired SSL certificates'
            }
        ]
    },

    // ===== EXTERNAL APIs =====
    github: {
        name: 'GitHub',
        category: IntegrationCategories.EXTERNAL_APIS,
        secretType: 'ApiKey',
        description: 'Source control and collaboration platform',
        fields: [
            {
                key: 'password',
                label: 'Personal Access Token',
                type: FieldTypes.PASSWORD,
                required: true,
                placeholder: 'github_pat_...',
                help: 'Generate from GitHub Settings → Developer settings → Personal access tokens (requires repo and workflow scopes)'
            },
            {
                key: 'username',
                label: 'Default Owner',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'Capital-Land-Management-Services',
                help: 'Default organization or username for repository operations'
            },
            {
                key: 'apiKey',
                label: 'Default Repository',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'CapTrac-ReactCore',
                help: 'Default repository name'
            }
        ]
    },

    sentry: {
        name: 'Sentry Error Tracking',
        category: IntegrationCategories.EXTERNAL_APIS,
        secretType: 'ApiKey',
        description: 'Application monitoring and error tracking (for IT Report production health metrics)',
        fields: [
            {
                key: 'apiKey',
                label: 'Auth Token',
                type: FieldTypes.PASSWORD,
                required: true,
                placeholder: 'sntrys_...',
                help: 'Sentry API auth token with project:read, event:read, and org:read scopes (Settings → Auth Tokens)'
            },
            {
                key: 'baseUrl',
                label: 'Sentry Instance URL',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'https://sentry.capland.co.za',
                help: 'URL of your Sentry instance (for self-hosted) or https://sentry.io (for cloud)'
            },
            {
                // Note: Using 'username' field to store org slug (follows AppSecret model structure)
                key: 'username',
                label: 'Organization Slug',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'capital-land-management-services',
                help: 'Sentry organization slug (optional - will use default from configuration if not specified)'
            },
            {
                // Note: Using 'password' field to store comma-separated project slugs (follows AppSecret model structure)
                key: 'password',
                label: 'Project Slugs',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'captrac-reactcore-api,captrac-reactcore-ui',
                help: 'Comma-separated list of Sentry project slugs to monitor (optional - will use defaults if not specified)'
            }
        ]
    },

    meteocontrol: {
        name: 'MeteoControl Weather',
        category: IntegrationCategories.EXTERNAL_APIS,
        secretType: 'ApiKey',
        description: 'Solar and weather data monitoring',
        fields: [
            {
                key: 'apiKey',
                label: 'API Key',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'X-API-KEY header value from MeteoControl'
            },
            {
                key: 'username',
                label: 'Basic Auth Username',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'api-user',
                help: 'Username for HTTP basic authentication'
            },
            {
                key: 'password',
                label: 'Basic Auth Password',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'Password for HTTP basic authentication'
            },
            {
                key: 'baseUrl',
                label: 'MeteoControl API URL',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'https://api.meteocontrol.de/v2',
                help: 'MeteoControl API endpoint'
            },
            {
                key: 'allowInsecure',
                label: 'Allow Insecure Connections',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable this if using self-signed or expired SSL certificates'
            }
        ]
    },

    retaildynamics: {
        name: 'RetailDynamics',
        category: IntegrationCategories.EXTERNAL_APIS,
        secretType: 'ApiKey',
        description: 'Foot traffic counter and retail analytics',
        fields: [
            {
                key: 'apiKey',
                label: 'API Key',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'RetailDynamics API authentication key'
            },
            {
                key: 'baseUrl',
                label: 'API URL',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'https://api.retaildynamics.com',
                help: 'RetailDynamics API endpoint'
            },
            {
                key: 'allowInsecure',
                label: 'Allow Insecure Connections',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable this if using self-signed or expired SSL certificates'
            }
        ]
    },

    gemini: {
        name: 'Google Gemini AI (Legacy)',
        category: IntegrationCategories.EXTERNAL_APIS,
        secretType: 'ApiKey',
        description: 'Legacy Gemini config - use AI Insights Configuration instead',
        deprecated: true,
        fields: [
            {
                key: 'apiKey',
                label: 'Gemini API Key',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'API key from Google AI Studio'
            },
            {
                key: 'modelName',
                label: 'Model Name',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'gemini-2.5-flash',
                help: 'Gemini model identifier (e.g., gemini-2.5-flash, gemini-pro)'
            },
            {
                key: 'isEnabled',
                label: 'Enable AI Insights',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable or disable AI-powered insights feature. When disabled, no AI calls will be made.'
            }
        ]
    },

    // ===== AZURE & CLOUD =====
    azuread: {
        name: 'Azure AD Application (General)',
        category: IntegrationCategories.AZURE_CLOUD,
        secretType: 'AzureAd',
        description: 'Microsoft Azure Active Directory app registration - for Defender, SharePoint, Exchange, Intune, Purview, and other Microsoft 365 services',
        fields: [
            {
                key: 'azureTenantId',
                label: 'Tenant ID',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: '5ddb235f-c524-4e6a-b1ea-821a5664eac5',
                help: 'Azure AD tenant GUID (not the full authority URL)'
            },
            {
                key: 'azureClientId',
                label: 'Client ID (Application ID)',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: '4d4fae28-b5f2-4aa6-8a8b-ea11d32d2ba0',
                help: 'Azure AD application (client) ID'
            },
            {
                key: 'azureClientSecret',
                label: 'Client Secret',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'Azure AD application client secret value'
            },
            {
                key: 'azureAuthority',
                label: 'Authority URL (Optional)',
                type: FieldTypes.URL,
                required: false,
                placeholder: 'https://login.microsoftonline.com/{tenant-id}',
                help: 'Leave empty to use default. Custom authority for specific clouds.'
            },
            {
                key: 'azureScopes',
                label: 'Scopes',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'https://graph.microsoft.com/.default',
                help: 'Comma-separated list of OAuth scopes'
            }
        ]
    },

    // ===== EMAIL & MESSAGING =====
    emailconfig: {
        name: 'Email Configuration',
        category: IntegrationCategories.EMAIL_MESSAGING,
        secretType: 'EmailConfig',
        description: 'Email sending and receiving configuration',
        fields: [
            {
                key: 'emailProvider',
                label: 'Email Provider',
                type: FieldTypes.SELECT,
                required: true,
                options: [
                    { value: 'SMTP', label: 'SMTP (Standard)' },
                    { value: 'Microsoft365', label: 'Microsoft 365 (Graph API)' },
                    { value: 'SendGrid', label: 'SendGrid' }
                ],
                help: 'Email service provider type'
            },
            {
                key: 'emailFromAddress',
                label: 'From Email Address',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'noreply@company.com',
                help: 'Email address used for sending messages'
            },
            {
                key: 'emailFromName',
                label: 'From Display Name',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'CapTrac Notifications',
                help: 'Display name shown to email recipients'
            },
            // SMTP-specific fields (conditionally shown)
            {
                key: 'smtpServer',
                label: 'SMTP Server',
                type: FieldTypes.TEXT,
                required: true,
                showIf: { field: 'emailProvider', values: ['SMTP'] },
                placeholder: 'smtp.office365.com',
                help: 'SMTP server hostname'
            },
            {
                key: 'smtpPort',
                label: 'SMTP Port',
                type: FieldTypes.NUMBER,
                required: true,
                showIf: { field: 'emailProvider', values: ['SMTP'] },
                placeholder: '587',
                help: 'SMTP server port (587 for TLS, 465 for SSL, 25 for unencrypted)'
            },
            {
                key: 'smtpUseSsl',
                label: 'Use SSL/TLS',
                type: FieldTypes.CHECKBOX,
                required: false,
                showIf: { field: 'emailProvider', values: ['SMTP'] },
                help: 'Enable secure connection (recommended)'
            },
            {
                key: 'username',
                label: 'SMTP Username',
                type: FieldTypes.TEXT,
                required: true,
                showIf: { field: 'emailProvider', values: ['SMTP'] },
                help: 'SMTP authentication username'
            },
            {
                key: 'password',
                label: 'SMTP Password',
                type: FieldTypes.PASSWORD,
                required: true,
                showIf: { field: 'emailProvider', values: ['SMTP'] },
                help: 'SMTP authentication password or app password'
            },
            // Microsoft 365 requires Azure AD (can use reference)
            {
                key: 'referencedSecretKey',
                label: 'Azure AD Credentials',
                type: FieldTypes.SELECT,
                required: false,
                showIf: { field: 'emailProvider', values: ['Microsoft365'] },
                help: 'Reference an existing AzureAd secret or configure below',
                dynamicOptions: 'azureAdSecrets'
            }
        ]
    },

    sharepointfilestorage: {
        name: 'SharePoint File Storage',
        category: IntegrationCategories.AZURE_CLOUD,
        secretType: 'AzureAd',
        description: 'SharePoint document library for file storage — all CapTrac file attachments are stored here via the Microsoft Graph API',
        fields: [
            {
                key: 'azureTenantId',
                label: 'Azure AD Tenant ID',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
                help: 'Azure AD tenant GUID (found in Azure Portal → Azure Active Directory → Overview)'
            },
            {
                key: 'azureClientId',
                label: 'Application (Client) ID',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
                help: 'Azure AD app registration client ID. Requires Sites.ReadWrite.All and Files.ReadWrite.All Graph API permissions'
            },
            {
                key: 'azureClientSecret',
                label: 'Client Secret',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'Azure AD application client secret value'
            },
            {
                key: 'baseUrl',
                label: 'SharePoint Site URL',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'https://yourtenant.sharepoint.com/sites/CapTrac',
                help: 'Full URL to the SharePoint site containing the document library'
            },
            {
                key: 'modelName',
                label: 'Document Library Name',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'Action Item Attachments',
                help: 'Name of the document library within the SharePoint site. All module subfolders are created inside this library'
            },
            {
                key: 'referencedSecretKey',
                label: 'Use Existing Azure AD Credentials',
                type: FieldTypes.SELECT,
                required: false,
                help: 'Reuse credentials from an existing Azure AD secret instead of entering them again',
                dynamicOptions: 'azureAdSecrets'
            }
        ]
    },

    // ===== COLLABORATION & MESSAGING =====

    msteams: {
        name: 'Microsoft Teams',
        category: IntegrationCategories.COLLABORATION,
        secretType: 'Configuration',
        description: 'Send action item notifications to Teams channels and users. Supports webhook and Graph API (Bot) modes',
        fields: [
            {
                key: 'provider',
                label: 'Integration Mode',
                type: FieldTypes.SELECT,
                required: true,
                options: [
                    { value: 'Webhook', label: 'Incoming Webhook (Simple — per-channel)' },
                    { value: 'GraphApi', label: 'Graph API Bot (Advanced — multi-channel, user DMs)' }
                ],
                help: 'Webhook is the simplest setup: paste a webhook URL for each channel. Graph API Bot allows sending to any channel or user via Azure AD app permissions'
            },
            // Webhook mode fields
            {
                key: 'baseUrl',
                label: 'Default Webhook URL',
                type: FieldTypes.URL,
                required: true,
                showIf: { field: 'provider', values: ['Webhook'] },
                placeholder: 'https://yourtenant.webhook.office.com/webhookb2/...',
                help: 'Incoming Webhook URL from Teams channel connector settings. This is the default channel for notifications'
            },
            {
                key: 'secondaryUrl',
                label: 'IT Alerts Webhook URL (Optional)',
                type: FieldTypes.URL,
                required: false,
                showIf: { field: 'provider', values: ['Webhook'] },
                placeholder: 'https://yourtenant.webhook.office.com/webhookb2/...',
                help: 'Separate webhook URL for IT-related notifications (ticket creation, SLA breaches). Leave empty to use default'
            },
            // Graph API Bot mode fields
            {
                key: 'referencedSecretKey',
                label: 'Azure AD Credentials',
                type: FieldTypes.SELECT,
                required: false,
                showIf: { field: 'provider', values: ['GraphApi'] },
                help: 'Reference an existing Azure AD secret. Requires TeamWork.Read.All, Chat.ReadWrite.All, ChannelMessage.Send permissions',
                dynamicOptions: 'azureAdSecrets'
            },
            {
                key: 'customerId',
                label: 'Default Team ID',
                type: FieldTypes.TEXT,
                required: false,
                showIf: { field: 'provider', values: ['GraphApi'] },
                placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
                help: 'Microsoft Teams Team ID for default notification channel (found in Teams → Get link to team)'
            },
            {
                key: 'apiKey',
                label: 'Default Channel ID',
                type: FieldTypes.TEXT,
                required: false,
                showIf: { field: 'provider', values: ['GraphApi'] },
                placeholder: '19:xxxx@thread.tacv2',
                help: 'Teams Channel ID within the team for default notifications'
            },
            // Common fields
            {
                key: 'isEnabled',
                label: 'Enable Teams Notifications',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'When enabled, action item events (creation, assignment, comments, status changes) are sent to Teams'
            },
            {
                key: 'domains',
                label: 'Notification Events',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'Created,Assigned,Commented,StatusChanged,SlaBreached',
                help: 'Comma-separated list of action item events that trigger Teams notifications. Leave empty for all events'
            }
        ]
    },

    whatsapp: {
        name: 'WhatsApp Business',
        category: IntegrationCategories.COLLABORATION,
        secretType: 'ApiKey',
        description: 'Send action item notifications via WhatsApp Business Platform (Meta Cloud API). Users receive updates on their phone',
        fields: [
            {
                key: 'apiKey',
                label: 'Permanent Access Token',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'System User access token from Meta Business Suite → Business Settings → System Users. Must have whatsapp_business_messaging permission'
            },
            {
                key: 'customerId',
                label: 'Phone Number ID',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: '123456789012345',
                help: 'WhatsApp Business Phone Number ID (from Meta Developer Portal → WhatsApp → API Setup)'
            },
            {
                key: 'username',
                label: 'WhatsApp Business Account ID',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: '123456789012345',
                help: 'WABA ID from Meta Business Suite → Business Settings → WhatsApp Accounts'
            },
            {
                key: 'baseUrl',
                label: 'Graph API URL',
                type: FieldTypes.URL,
                required: false,
                placeholder: 'https://graph.facebook.com/v21.0',
                help: 'Meta Graph API base URL. Defaults to latest stable version if left empty'
            },
            {
                key: 'password',
                label: 'Webhook Verify Token',
                type: FieldTypes.PASSWORD,
                required: false,
                help: 'Custom verify token for webhook verification. Used when Meta sends webhook subscription confirmation'
            },
            {
                key: 'modelName',
                label: 'Default Template Name',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'captrac_action_item_update',
                help: 'Pre-approved WhatsApp message template name for notifications. Templates must be approved in Meta Business Manager before use'
            },
            {
                key: 'isEnabled',
                label: 'Enable WhatsApp Notifications',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'When enabled, users with a WhatsApp-registered phone number can receive action item notifications via WhatsApp'
            },
            {
                key: 'domains',
                label: 'Notification Events',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'Created,Assigned,Commented,StatusChanged,SlaBreached',
                help: 'Comma-separated list of action item events that trigger WhatsApp notifications. Leave empty for all events'
            }
        ]
    },

    // ===== REMOTE SUPPORT =====

    splashtop: {
        name: 'Splashtop Remote Support',
        category: IntegrationCategories.REMOTE_SUPPORT,
        secretType: 'Configuration',
        description: 'Remote desktop access for IT support. Supports Direct Launch (SOS/Business — no API keys) or API mode (Business with cloud API)',
        fields: [
            {
                key: 'username',
                label: 'Connection Mode',
                type: FieldTypes.SELECT,
                required: true,
                options: [
                    { value: 'direct', label: 'Direct Launch (SOS / Business) — No API keys required' },
                    { value: 'api', label: 'API Mode (Business only) — Requires API key & secret' }
                ],
                help: 'Direct Launch uses the locally-installed Splashtop app to connect. API Mode uses the Splashtop Cloud API for device lookup (requires a Business subscription with API access)'
            },
            {
                key: 'secondaryUrl',
                label: 'Connection URI Protocol',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'st-business://',
                help: 'Custom URI protocol registered by the Splashtop app on the technician\'s machine. Default: st-business:// (Splashtop Business) or st-sos:// (SOS). Leave empty to use st-business://'
            },
            {
                key: 'apiKey',
                label: 'API Key (API Mode only)',
                type: FieldTypes.PASSWORD,
                required: false,
                showIf: { field: 'username', values: ['api'] },
                help: 'Splashtop Business API key from my.splashtop.com → Management → API. Only required for API Mode'
            },
            {
                key: 'password',
                label: 'API Secret (API Mode only)',
                type: FieldTypes.PASSWORD,
                required: false,
                showIf: { field: 'username', values: ['api'] },
                help: 'Splashtop Business API secret (paired with the API key). Only required for API Mode'
            },
            {
                key: 'baseUrl',
                label: 'API Base URL (API Mode only)',
                type: FieldTypes.URL,
                required: false,
                showIf: { field: 'username', values: ['api'] },
                placeholder: 'https://my.splashtop.com/csapi/v2.0',
                help: 'Splashtop Cloud API endpoint. Only required for API Mode'
            },
            {
                key: 'customerId',
                label: 'Team ID (Optional)',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: '123456',
                help: 'Splashtop team/group ID to scope device lookups. Leave empty to search all teams'
            },
            {
                key: 'isEnabled',
                label: 'Enable Remote Connect',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'When enabled, IT tickets show a "Remote Connect" button that launches a Splashtop session to the user\'s last known device'
            },
            {
                key: 'allowInsecure',
                label: 'Allow Insecure Connections',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable this if using self-signed SSL certificates on an on-premise Splashtop server'
            }
        ]
    },

    // ===== CONFIGURATION =====
    ollama: {
        name: 'Ollama AI (Legacy)',
        category: IntegrationCategories.CONFIGURATION,
        secretType: 'Configuration',
        description: 'Legacy Ollama config - use AI Insights Configuration instead',
        deprecated: true,
        fields: [
            {
                key: 'baseUrl',
                label: 'Ollama Server URL',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'http://172.16.16.100:11434',
                help: 'URL to your Ollama server instance'
            },
            {
                key: 'modelName',
                label: 'Model Name',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'llama3.3:70b',
                help: 'Ollama model to use (e.g., llama3.3:70b, tinyllama:1.1b)'
            },
            {
                key: 'timeoutSeconds',
                label: 'Timeout (seconds)',
                type: FieldTypes.NUMBER,
                required: false,
                placeholder: '120',
                help: 'API request timeout in seconds'
            },
            {
                key: 'allowInsecure',
                label: 'Allow Insecure Connections',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable this if using self-signed or expired SSL certificates'
            }
        ]
    },

    insights: {
        name: 'AI Insights Configuration',
        category: IntegrationCategories.CONFIGURATION,
        secretType: 'Configuration',
        description: 'Unified AI-powered insights configuration supporting multiple providers',
        fields: [
            {
                key: 'isEnabled',
                label: 'Enable AI Insights',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Toggle AI insights feature on/off. When disabled, no AI calls will be made.'
            },
            {
                key: 'provider',
                label: 'AI Provider',
                type: FieldTypes.SELECT,
                required: true,
                options: [
                    { value: 'Gemini', label: 'Google Gemini' },
                    { value: 'Ollama', label: 'Ollama (Local)' },
                    { value: 'Groq', label: 'Groq' }
                ],
                help: 'AI service provider for insights'
            },
            // Google Gemini Configuration
            {
                key: 'apiKey',
                label: 'Gemini API Key',
                type: FieldTypes.PASSWORD,
                required: true,
                showIf: { field: 'provider', values: ['Gemini'] },
                help: 'API key from Google AI Studio (https://aistudio.google.com/app/apikey)'
            },
            {
                key: 'modelName',
                label: 'Gemini Model',
                type: FieldTypes.TEXT,
                required: false,
                showIf: { field: 'provider', values: ['Gemini'] },
                placeholder: 'gemini-2.5-flash',
                help: 'Gemini model identifier (e.g., gemini-2.5-flash, gemini-pro, gemini-1.5-pro)'
            },
            // Ollama Configuration
            {
                key: 'baseUrl',
                label: 'Ollama Server URL',
                type: FieldTypes.URL,
                required: true,
                showIf: { field: 'provider', values: ['Ollama'] },
                placeholder: 'http://172.16.16.100:11434',
                help: 'URL to your Ollama server instance'
            },
            {
                key: 'modelName',
                label: 'Ollama Model',
                type: FieldTypes.TEXT,
                required: true,
                showIf: { field: 'provider', values: ['Ollama'] },
                placeholder: 'llama3.3:70b',
                help: 'Ollama model to use (e.g., llama3.3:70b, tinyllama:1.1b, mistral:7b)'
            },
            // Groq Configuration
            {
                key: 'apiKey',
                label: 'Groq API Key',
                type: FieldTypes.PASSWORD,
                required: true,
                showIf: { field: 'provider', values: ['Groq'] },
                help: 'API key from Groq Console (https://console.groq.com/keys)'
            },
            {
                key: 'modelName',
                label: 'Groq Model',
                type: FieldTypes.TEXT,
                required: false,
                showIf: { field: 'provider', values: ['Groq'] },
                placeholder: 'llama-3.3-70b-versatile',
                help: 'Groq model identifier (e.g., llama-3.3-70b-versatile, mixtral-8x7b-32768, gemma2-9b-it)'
            },
            // Common Configuration
            {
                key: 'timeoutSeconds',
                label: 'Timeout (seconds)',
                type: FieldTypes.NUMBER,
                required: false,
                placeholder: '120',
                help: 'API request timeout in seconds'
            },
            {
                key: 'maxChars',
                label: 'Max Characters',
                type: FieldTypes.NUMBER,
                required: false,
                placeholder: '10000',
                help: 'Maximum characters per analysis group'
            },
            {
                key: 'maxTokens',
                label: 'Max Output Tokens',
                type: FieldTypes.NUMBER,
                required: false,
                placeholder: '2000',
                help: 'Maximum tokens in AI response'
            },
            {
                key: 'allowInsecure',
                label: 'Allow Insecure Connections',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable this if using self-signed or expired SSL certificates (for Ollama servers)'
            }
        ]
    },

    exchange: {
        name: 'Exchange Configuration',
        category: IntegrationCategories.CONFIGURATION,
        secretType: 'Configuration',
        description: 'Microsoft Exchange/365 email configuration',
        fields: [
            {
                key: 'domains',
                label: 'Internal Domains',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'company.com,company.co.za',
                help: 'Comma-separated list of internal email domains'
            },
            {
                key: 'maxParallelism',
                label: 'Max Parallel Requests',
                type: FieldTypes.NUMBER,
                required: false,
                placeholder: '5',
                help: 'Number of concurrent API requests'
            },
            {
                key: 'pageSize',
                label: 'Page Size',
                type: FieldTypes.NUMBER,
                required: false,
                placeholder: '100',
                help: 'Number of items per API page'
            },
            {
                key: 'referencedSecretKey',
                label: 'Azure AD Credentials',
                type: FieldTypes.SELECT,
                required: false,
                help: 'Reference an existing AzureAd secret for authentication',
                dynamicOptions: 'azureAdSecrets'
            }
        ]
    },

    // ===== DATABASES =====
    mysql: {
        name: 'MySQL Database',
        category: IntegrationCategories.DATABASES,
        secretType: 'MySqlConnection',
        description: 'MySQL database connection configuration',
        fields: [
            {
                key: 'mySqlServer',
                label: 'Server Address',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'localhost or 10.0.0.5',
                help: 'MySQL server hostname or IP address'
            },
            {
                key: 'mySqlPort',
                label: 'Port',
                type: FieldTypes.NUMBER,
                required: true,
                placeholder: '3306',
                help: 'MySQL server port'
            },
            {
                key: 'mySqlDatabase',
                label: 'Database Name',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'my_database',
                help: 'Name of the database to connect to'
            },
            {
                key: 'username',
                label: 'Username',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'db_user',
                help: 'Database user account'
            },
            {
                key: 'password',
                label: 'Password',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'Database user password'
            },
            {
                key: 'mySqlOptions',
                label: 'Additional Options',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'Charset=utf8mb4;SslMode=Required',
                help: 'Additional connection string parameters'
            }
        ]
    },

    mssql: {
        name: 'MS SQL Server Database',
        category: IntegrationCategories.DATABASES,
        secretType: 'MsSqlConnection',
        description: 'Microsoft SQL Server connection configuration',
        fields: [
            {
                key: 'msSqlServer',
                label: 'Server Address',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'SERVER\\INSTANCE or 10.0.0.5,1433',
                help: 'SQL Server name with instance or IP with port'
            },
            {
                key: 'msSqlDatabase',
                label: 'Database Name',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'MyDatabase',
                help: 'Name of the database to connect to'
            },
            {
                key: 'msSqlUseWindowsAuth',
                label: 'Use Windows Authentication',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Use Windows/Integrated Authentication instead of SQL Authentication'
            },
            {
                key: 'username',
                label: 'SQL Username',
                type: FieldTypes.TEXT,
                required: false,
                showIf: { field: 'msSqlUseWindowsAuth', values: [false, undefined, null] },
                placeholder: 'sa',
                help: 'SQL Server authentication username (not needed for Windows Auth)'
            },
            {
                key: 'password',
                label: 'SQL Password',
                type: FieldTypes.PASSWORD,
                required: false,
                showIf: { field: 'msSqlUseWindowsAuth', values: [false, undefined, null] },
                help: 'SQL Server authentication password (not needed for Windows Auth)'
            },
            {
                key: 'msSqlEncrypt',
                label: 'Encrypt Connection',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Enable connection encryption (recommended for production)'
            },
            {
                key: 'msSqlTrustServerCertificate',
                label: 'Trust Server Certificate',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Accept self-signed certificates (only for development)'
            },
            {
                key: 'msSqlConnectionTimeout',
                label: 'Connection Timeout',
                type: FieldTypes.NUMBER,
                required: false,
                placeholder: '30',
                help: 'Timeout in seconds for establishing connection'
            }
        ]
    },

    influxdb: {
        name: 'InfluxDB',
        category: IntegrationCategories.DATABASES,
        secretType: 'ApiKey',
        description: 'InfluxDB time-series database for router monitoring and SNMP metrics storage',
        fields: [
            {
                key: 'baseUrl',
                label: 'InfluxDB URL',
                type: FieldTypes.URL,
                required: true,
                placeholder: 'http://172.16.16.33:8086',
                help: 'Full URL of your InfluxDB instance including port'
            },
            {
                key: 'apiKey',
                label: 'API Token',
                type: FieldTypes.PASSWORD,
                required: true,
                help: 'InfluxDB API token with write permissions for the target bucket'
            },
            {
                key: 'customerId',
                label: 'Organization',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'CapLand',
                help: 'InfluxDB organization name (not ID)'
            },
            {
                key: 'modelName',
                label: 'Bucket',
                type: FieldTypes.TEXT,
                required: true,
                placeholder: 'CapTrac_dev',
                help: 'InfluxDB bucket name where router monitoring metrics are written'
            }
        ]
    },

    // ===== NOTIFICATION ROUTING (used internally, not for direct secret creation) =====
    notificationrouting: {
        name: 'Notification Channel Routing',
        category: IntegrationCategories.CONFIGURATION,
        secretType: 'Configuration',
        description: 'Internal configuration for routing action item notifications across channels (Email, Teams, WhatsApp)',
        internal: true,
        fields: [
            {
                key: 'isEnabled',
                label: 'Enable Multi-Channel Notifications',
                type: FieldTypes.CHECKBOX,
                required: false,
                help: 'Master toggle for the multi-channel notification system. When disabled, only email notifications are sent'
            },
            {
                key: 'domains',
                label: 'Default Channels',
                type: FieldTypes.TEXT,
                required: false,
                placeholder: 'Email,Teams',
                help: 'Comma-separated list of default notification channels for new users (Email, Teams, WhatsApp)'
            },
            {
                key: 'provider',
                label: 'Fallback Channel',
                type: FieldTypes.SELECT,
                required: false,
                options: [
                    { value: 'Email', label: 'Email (Always reliable)' },
                    { value: 'Teams', label: 'Microsoft Teams' },
                    { value: 'WhatsApp', label: 'WhatsApp' }
                ],
                help: 'If primary notification channel fails, fall back to this channel'
            }
        ]
    }
};

/**
 * Get integration configuration by secret key
 * @param {string} secretKey - The secret key to lookup (case-insensitive)
 * @returns {object|null} Integration configuration or null if not found
 */
export function getIntegrationConfig(secretKey) {
    if (!secretKey) return null;
    
    const lowerKey = secretKey.toLowerCase();
    
    // Try exact match first
    if (IntegrationConfigs[lowerKey]) {
        return IntegrationConfigs[lowerKey];
    }
    
    // Try prefix match with dot delimiter (e.g., "porta.customer1" matches "porta")
    // This ensures we only match on word boundaries to avoid false positives
    for (const [key, config] of Object.entries(IntegrationConfigs)) {
        if (lowerKey.startsWith(key + '.')) {
            return config;
        }
    }
    
    return null;
}

/**
 * Get all integrations grouped by category
 * @returns {object} Integrations grouped by category
 */
export function getIntegrationsByCategory() {
    const grouped = {};
    
    for (const [key, config] of Object.entries(IntegrationConfigs)) {
        const category = config.category || 'Other';
        if (!grouped[category]) {
            grouped[category] = [];
        }
        grouped[category].push({
            key,
            ...config
        });
    }
    
    return grouped;
}

/**
 * Get field value from secret object
 * @param {object} secret - The secret object
 * @param {string} fieldKey - The field key to retrieve
 * @returns {any} Field value
 */
export function getFieldValue(secret, fieldKey) {
    if (!secret) return '';
    return secret[fieldKey] ?? '';
}

/**
 * Check if a field should be shown based on conditions
 * @param {object} field - Field configuration
 * @param {object} secret - Current secret values
 * @returns {boolean} Whether field should be shown
 */
export function shouldShowField(field, secret) {
    if (!field.showIf) return true;
    
    const conditionField = field.showIf.field;
    const conditionValues = field.showIf.values;
    const actualValue = secret[conditionField];
    
    return conditionValues.includes(actualValue);
}
